import * as React from 'react';
import { Suspense } from 'react';
import { QueryClientProvider } from 'react-query';
import { ReactQueryDevtools } from 'react-query/devtools';
import { Route, RouteComponentProps, Switch } from 'react-router-dom';
import AuthenticationWrapper from 'src/components/AuthenticationWrapper';
import SuspenseLoader from 'src/components/SuspenseLoader';
import TopMenu from 'src/features/TopMenu';
import LoggedInCallback from 'src/layouts/LoggedInCallback';
import Logout from 'src/layouts/Logout';
import App from 'src/App';
import AppThemeWrapper from 'src/AppThemeWrapper';
import { queryClient } from 'src/queries/base';
import { ThemeProvider } from '@mui/material/styles';
import { SnackbarProvider } from 'notistack';
import CloseIcon from 'src/assets/icons/warning_error.svg';
import CheckIcon from 'src/assets/icons/check.svg';
import WarningIcon from 'src/assets/icons/warning.svg';
import ApplyLanding from 'src/features/Auth/SignUp/ApplyLanding';
import MagicLanding from 'src/features/Auth/SignUp/MagicLanding';
import AdvisorProductLanding from 'src/features/LoansHome/AdvisorProductLanding'
import SiteSettings from 'src/siteSetting';
import { loadThemes } from 'src/theme_mui';
import { DEFAULT_HOME_ROOT, LOG_OUT } from "src/route_constants";
import DateAdapter from '@mui/lab/AdapterDateFns';
import LocalizationProvider from '@mui/lab/LocalizationProvider';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import 'src/assets/stylesheets/desktop.css';
import { PASSWORDLESS_ROUTE, PASSWORD_UPDATE_ROUTE } from 'src/route_constants';
import { OAUTH_CALLBACK } from 'src/route_constants';

import MoxoLauncher from 'src/pages/Applications/commons/Moxo';


const renderApp = (props: RouteComponentProps) => (
  <React.Fragment>
    <AppThemeWrapper>
      <App />
    </AppThemeWrapper>
  </React.Fragment>
);

const SignUpLanding = React.lazy(
  () => import('src/features/Auth/SignUp'));

const GetStartedProductLanding = React.lazy(
  () => import('src/features/LoansHome/SiteProductLanding'));

const LoginLanding = React.lazy(
  () => import('src/features/Auth/Login')
);

const SSOLoginLanding = React.lazy(
  () => import('src/features/Auth/SSO')
);

const UsersLanding = React.lazy(() => import('src/features/Authly'));

const Auth0Redirect = React.lazy(
  () => import('src/features/Auth/Auth0SignIn/Auth0RedirectLanding')
)

const NoAuthLanding = React.lazy(
  () => import('src/features/NoAuth/NoAuthLanding')
)

const PasswordExpiryLanding = React.lazy(
  () => import("src/features/Authly/Passwords/AccountRecover/PasswordExpiryReset/PasswordExpiryLanding")
)

const HeroLanding = React.lazy(
  () => import("src/features/Home/HomeLanding")
)

const LoanProductLanding = React.lazy(() => import('src/features/LoansHome/LoanProductLanding'))

const RatesWidget = React.lazy(
  () => import('src/features/Rates/RatesLanding')
);

const RatesLanding = React.lazy(
  () => import('src/features/Rates')
);


const routes = () => (
  <Suspense fallback={<SuspenseLoader />}>
    <QueryClientProvider client={queryClient}>
      <SnackbarProvider
        maxSnack={3}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        iconVariant={{
          success: <CheckIcon color="#FFF" />,
          error: <CloseIcon color="#FFF" />,
          warning: <WarningIcon color="#FFF" />,
        }}
      >
        <ThemeProvider theme={loadThemes(SiteSettings)}>
          <DndProvider backend={HTML5Backend}>
            <LocalizationProvider dateAdapter={DateAdapter}>
              <Switch>
                <Route exact path="/" component={HeroLanding} />
                <Route exact path="/login" component={LoginLanding} />
                <Route exact path="/sso" component={SSOLoginLanding} />
                <Route exact path="/internal/login" component={LoginLanding} />
                {SiteSettings.generalGetstartedWithTilesEnabled() &&
                  <Route path="/signup" component={GetStartedProductLanding} />
                }
                {!SiteSettings.generalGetstartedWithTilesEnabled() &&
                  <Route exact path="/signup" component={SignUpLanding} />
                }
                {SiteSettings.generalGetstartedWithTilesEnabled() &&
                  <Route exact path={`${DEFAULT_HOME_ROOT}`} component={GetStartedProductLanding} />
                }
                {!SiteSettings.generalGetstartedWithTilesEnabled() &&
                  <Route exact path={DEFAULT_HOME_ROOT} component={SignUpLanding} />
                }
                <Route exact path={`${DEFAULT_HOME_ROOT}/:loanType`} component={LoanProductLanding} />
                <Route exact path={OAUTH_CALLBACK} component={LoggedInCallback} />
                <Route exact path="/apply/:username" component={ApplyLanding} />
                <Route exact path="/apply/:username/:loanType" component={ApplyLanding} />
                {SiteSettings.generalRatesEnabled() &&
                  <Route exact path="/rates" component={RatesWidget} />
                }
                {SiteSettings.generalRatesEnabled() &&
                  <Route exact path="/mortgage/rates" component={RatesLanding} />
                }
                <Route path="/loanproducts" component={AdvisorProductLanding} />
                <Route path="/login/magictoken/:token" component={MagicLanding} />
                <Route path="/u" component={UsersLanding} />
                <Route path='/sso/callback' component={Auth0Redirect} />
                <Route path={`${PASSWORDLESS_ROUTE}/:loanType`} component={NoAuthLanding} />
                <AuthenticationWrapper>
                  <TopMenu user />
                  <Switch>
                    <Route exact path={LOG_OUT} component={Logout} />
                    <Route exact path={PASSWORD_UPDATE_ROUTE} component={PasswordExpiryLanding} />
                    <Route render={renderApp} />
                  </Switch>

                  <MoxoLauncher />
                </AuthenticationWrapper>
              </Switch>
              <ReactQueryDevtools
                initialIsOpen={false}
                toggleButtonProps={{ style: { marginLeft: '3em' } }}
              />
            </LocalizationProvider>
          </DndProvider>
        </ThemeProvider>
      </SnackbarProvider>
    </QueryClientProvider>
  </Suspense>
);

export default routes;
